import { Routes } from '@angular/router'

import { NotFoundComponent } from 'app/_shared/components/not-found/not-found.component'
import { OKTA_LOGIN_CALLBACK_PATH } from 'app/_shared/constants/okta'
import { AuthVerifiedGuard } from 'app/_shared/guards/auth-verified.guard'
import { canViewOrdersGuard } from 'app/_shared/guards/canViewOrders.guard'
import { canViewStoreCreditsGuard } from 'app/_shared/guards/canViewStoreCredits.guard'
import { isAdminGuard } from 'app/_shared/guards/isAdmin.guard'
import { KitGuard } from 'app/_shared/guards/kit.guard'
import { NoAuthGuard } from 'app/_shared/guards/no-auth.guard'
import { OktaCallbackGuard } from 'app/_shared/guards/okta-callback.guard'
import { RequiredProducts } from 'app/_shared/guards/required-products.guard'
import { OktaCallbackComponent } from 'app/pages/auth/okta-callback/okta-callback.component'
import { PrivateLegalComponent } from 'app/pages/auth/private-legal/private-legal.component'
import { PrivateComponent } from 'app/pages/auth/private/private.component'
import { CategoriesComponent } from 'app/pages/categories/categories.component'
import { ContactComponent } from 'app/pages/contact/contact.component'
import { KitComponent } from 'app/pages/kit/kit.component'
import { OrderConfirmationPageComponent } from 'app/pages/order-confirmation-page/order-confirmation-page.component'
import { PrivacyPolicyComponent } from 'app/pages/privacy-policy/privacy-policy.component'
import { TermsAndConditionsComponent } from 'app/pages/terms-and-conditions/terms-and-conditions.component'

import { HomeComponent } from './components/home/home.component'
import { AbandonedCartRecoveryComponent } from './pages/abandoned-cart-recovery/abandoned-cart-recovery.component'
import { AccountComponent } from './pages/account/account.component'
import { GiftCardsComponent } from './pages/account/gift-cards/gift-cards.component'
import { OrdersComponent } from './pages/account/orders/orders.component'
import { EmailVerificationComponent } from './pages/auth/email-verification/email-verification.component'
import { LoginComponent } from './pages/auth/login/login.component'
import { UpdatePasswordComponent } from './pages/auth/update-password/update-password.component'
import { CartComponent } from './pages/cart/cart.component'
import { ProductListComponent } from './pages/product-list/product-list.component'
import { ProductComponent } from './pages/product/product.component'
import { RegisterComponent } from './pages/register/register.component'
import { ResetPasswordSuccessComponent } from './pages/reset-password-success/reset-password-success.component'
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component'
import { UnderConstructionComponent } from './pages/under-construction/under-construction.component'
import { UnsubscribeReminderCouponsComponent } from './pages/unsubscribe-reminder-coupons/unsubscribe-reminder-coupons.component'

export const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    children: [
      // Auth routes (unguarded - always accessible)
      // Okta callback: rendered only as a fallback spinner; the real token
      // exchange happens in `AppConfigService.Init()` (APP_INITIALIZER).
      // `OktaCallbackGuard` only allows this route during a real Okta redirect
      // and otherwise sends the user back to `/` or `/login`.
      {
        path: OKTA_LOGIN_CALLBACK_PATH,
        component: OktaCallbackComponent,
        canActivate: [OktaCallbackGuard]
      },
      { path: 'login', component: LoginComponent, canActivate: [NoAuthGuard] },
      { path: 'register', component: RegisterComponent, canActivate: [NoAuthGuard] },
      { path: 'reset-password', component: ResetPasswordComponent },
      { path: 'reset-password-success', component: ResetPasswordSuccessComponent },
      { path: 'email-verification', component: EmailVerificationComponent },
      { path: 'update-password', component: UpdatePasswordComponent },
      { path: 'privacy-policy', component: PrivacyPolicyComponent },
      { path: 'terms-and-conditions', component: TermsAndConditionsComponent },
      { path: 'contact', component: ContactComponent },
      { path: 'under-construction', component: UnderConstructionComponent },
      { path: 'unsubscribe/reminders/:token', component: UnsubscribeReminderCouponsComponent },
      {
        path: 'order-confirmation/:uuid',
        component: OrderConfirmationPageComponent,
        canActivate: [AuthVerifiedGuard]
      },
      { path: 'kit', component: KitComponent, canDeactivate: [RequiredProducts] },
      {
        path: 'products',
        children: [
          { path: '', component: ProductListComponent },
          { path: ':slug', component: ProductComponent, canActivate: [KitGuard] }
        ]
      },
      {
        path: 'categories/:slug',
        component: CategoriesComponent
      },
      { path: 'abandoned-cart/:id', component: AbandonedCartRecoveryComponent },
      { path: 'cart', component: CartComponent },
      {
        path: 'checkout',
        loadComponent: () =>
          import('./pages/checkout/checkout.component').then(m => m.CheckoutComponent)
      },
      {
        path: 'my-account',
        component: AccountComponent,
        data: { usePageBackground: false },
        canActivate: [AuthVerifiedGuard],
        children: [
          { path: '', redirectTo: 'orders', pathMatch: 'full' },
          {
            path: 'orders',
            children: [
              { path: '', component: OrdersComponent },
              {
                path: ':uuid',
                component: OrderConfirmationPageComponent,
                data: { inAccount: true }
              }
            ]
          },
          { path: 'gift-cards', component: GiftCardsComponent },
          {
            path: 'admin',
            canActivateChild: [isAdminGuard],
            children: [
              {
                path: 'users',
                canActivate: [isAdminGuard],
                canActivateChild: [isAdminGuard],
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/account/admin/users/users.component').then(
                        m => m.UsersComponent
                      )
                  },
                  {
                    path: ':userId',
                    loadComponent: () =>
                      import('app/pages/account/admin/user-editor/user-editor.component').then(
                        m => m.UserEditorComponent
                      )
                  }
                ]
              },
              {
                path: 'orders',
                canActivateChild: [canViewOrdersGuard],
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('app/pages/account/admin/order-list/order-list.component').then(
                        m => m.OrderListComponent
                      )
                  },
                  {
                    path: ':orderId',
                    loadComponent: () =>
                      import('app/pages/account/admin/order-view/order-view.component').then(
                        m => m.OrderViewComponent
                      )
                  }
                ]
              },
              {
                path: 'store-credits',
                canActivateChild: [canViewStoreCreditsGuard],
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('app/pages/account/admin/store-credits/store-credits.component').then(
                        m => m.StoreCreditsComponent
                      )
                  },
                  {
                    path: ':storeCreditId',
                    loadComponent: () =>
                      import('app/pages/account/admin/store-credits-view/store-credits-view.component').then(
                        m => m.StoreCreditsViewComponent
                      )
                  }
                ]
              },
              {
                path: 'reports',
                loadComponent: () =>
                  import('app/pages/account/admin/reports/reports.component').then(
                    m => m.ReportsComponent
                  )
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: 'private/privacy-policy',
    component: PrivateLegalComponent,
    data: { legalPage: 'privacy-policy' }
  },
  {
    path: 'private/terms-and-conditions',
    component: PrivateLegalComponent,
    data: { legalPage: 'terms-and-conditions' }
  },
  { path: 'private', component: PrivateComponent, canActivate: [NoAuthGuard] },
  { path: 'not-found', component: NotFoundComponent },
  { path: '**', redirectTo: '' }
]
